import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { useTranslation } from "react-i18next";
import { auth, functions } from "@/src/config/firebase";
import { Skeleton } from "@/src/components/Skeleton";

type CreditGrantStatus = "active" | "pending" | "expired" | "voided";

/**
 * Mirrors the `listCreditGrants` callable's payload in full, including the
 * fields this table does not render (`status`, `category`, `effectiveAt`,
 * `voidedAt`, `metadata`). Keeping the mirror complete documents the contract
 * and means a stale client and a fresh backend never disagree about its shape.
 */
interface CreditGrantSummary {
  id: string;
  name: string | null;
  amountValue: number;
  /** Remaining balance on the grant; null when the ledger couldn't be read. */
  availableValue: number | null;
  /** Amount consumed/expired (amount − available); null when unknown. */
  usedValue: number | null;
  amountCurrency: string;
  /** Stripe grant category (`paid` / `promotional`); open-ended upstream. */
  category: string;
  effectiveAt: number | null;
  expiresAt: number | null;
  voidedAt: number | null;
  createdAt: number;
  status: CreditGrantStatus;
  metadata: Record<string, string>;
}

interface ListCreditGrantsResult {
  grants: CreditGrantSummary[];
}

function formatStripeAmount(value: number, currency: string, locale: string) {
  // Stripe returns the smallest currency unit (e.g. cents). We assume the
  // standard 100:1 ratio for major currencies; rare zero-decimal currencies
  // (e.g. JPY) would render off by 100, but we don't sell in those.
  const major = value / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(major);
  } catch {
    // Fallback if currency code is unrecognized.
    return `${major.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function formatDate(epochSeconds: number | null, locale: string) {
  if (!epochSeconds) return null;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(epochSeconds * 1000));
}

export const CreditGrantsTable: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "en";

  const { data, isLoading, isError } = useQuery({
    queryKey: ["ListCreditGrants", auth.currentUser?.uid],
    queryFn: async () => {
      const listCreditGrants = httpsCallable<
        Record<string, never>,
        ListCreditGrantsResult
      >(functions, "listCreditGrants", { timeout: 60_000 });
      const { data } = await listCreditGrants({});
      return data;
    },
    enabled: !!auth.currentUser,
  });

  return (
    <section className="min-w-0 lg:col-span-2">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        {t("creditGrants.heading")}
      </h2>
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <p className="text-sm text-gray-700 mb-4">
          {t("creditGrants.description")}
        </p>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full rounded-lg" />
            <Skeleton className="h-9 w-full rounded-lg" />
            <Skeleton className="h-9 w-full rounded-lg" />
          </div>
        ) : isError ? (
          <p className="text-sm text-red-600">{t("creditGrants.errorLoad")}</p>
        ) : !data || data.grants.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center">
            <p className="text-sm font-medium text-gray-700">
              {t("creditGrants.emptyTitle")}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {t("creditGrants.emptyHint")}
            </p>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    {t("creditGrants.colName")}
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">
                    {t("creditGrants.colAmount")}
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">
                    {t("creditGrants.colAvailable")}
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">
                    {t("creditGrants.colUsed")}
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    {t("creditGrants.colGranted")}
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    {t("creditGrants.colExpires")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {data.grants.map((grant) => (
                  <tr
                    key={grant.id}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <td className="py-3 px-4 text-gray-900">
                      <span className="font-medium">
                        {grant.name ?? t("creditGrants.unnamed")}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-gray-900">
                      {formatStripeAmount(
                        grant.amountValue,
                        grant.amountCurrency,
                        locale
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-900">
                      {grant.availableValue === null
                        ? "—"
                        : formatStripeAmount(
                            grant.availableValue,
                            grant.amountCurrency,
                            locale
                          )}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-700">
                      {grant.usedValue === null
                        ? "—"
                        : formatStripeAmount(
                            grant.usedValue,
                            grant.amountCurrency,
                            locale
                          )}
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {formatDate(grant.createdAt, locale) ?? "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {formatDate(grant.expiresAt, locale) ??
                        t("creditGrants.neverExpires")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};
